import { keyBy, mapValues } from 'lodash';
import { fieldTypes } from '../field-types';
import type { FieldTypeCard } from '../field-types/card';
import { CURRENT_FORMAT_VERSION } from './migrations';
import { condition, id, validation, type Condition, type Validation } from './pieces';
import { FIELD_WIDTHS, type FieldStyles } from './fieldWidth';
import { check, s, type Infer, type Schema } from './schema';

type RowElement = FieldElement | Infer<typeof staticText>;

export type RepeatableGroupField = FieldElement & { elements: RowElement[] };

export type FieldElement = {
  id: string;
  kind: 'field';
  type: string;
  label: string;
  description?: string;
  defaultValue?: unknown;
  settings?: object;
  styles?: FieldStyles;
  elements?: RowElement[];
  visibleWhen?: Condition;
  validations?: Validation[];
};

type FieldValue = Infer<(typeof fieldTypes)[number]['value']>;
export type SubmissionValues = { [fieldId: string]: FieldValue };

const staticText = s.strictObject({
  id,
  kind: s.literal('staticText'),
  text: s.string(),
  visibleWhen: condition.optional(),
});

const fieldOf = (card: FieldTypeCard, rowElement: Schema<unknown> | undefined) => {
  const shape: Record<string, Schema<unknown>> = {
    id,
    kind: s.literal('field'),
    type: s.literal(card.type),
    label: s.string(),
    description: s.string().optional(),
    styles: s.strictObject({ width: s.enum(FIELD_WIDTHS).optional() }).optional(),
    ...(card.hasDefaultValue ? { defaultValue: card.value.optional() } : {}),
    settings: check(card.settings, {}).ok ? card.settings.optional() : card.settings,
    visibleWhen: condition.optional(),
    validations: s.array(validation).optional(),
    ...(rowElement ? { elements: s.array(rowElement) } : {}),
  };
  return s.strictObject(shape) as Schema<FieldElement>;
};

const fieldsByType = (cards: FieldTypeCard[], rowElement?: Schema<unknown>) =>
  s.byKey(
    'type',
    mapValues(keyBy(cards, 'type'), card => fieldOf(card, card.valueKind === 'rows' ? rowElement : undefined)),
  );

const rowElement = s.byKey('kind', {
  field: fieldsByType(fieldTypes.filter(card => card.valueKind !== 'rows')),
  staticText,
});

const field = fieldsByType(fieldTypes, rowElement);

const section = s.strictObject({
  id,
  kind: s.literal('section'),
  title: s.string(),
  description: s.string().optional(),
  visibleWhen: condition.optional(),
  elements: s.array(s.byKey('kind', { field, staticText })),
});

const element = s.byKey('kind', { field, section, staticText });

export const formDefinition = s.strictObject({
  formatVersion: s.literal(CURRENT_FORMAT_VERSION),
  title: s.string(),
  description: s.string().optional(),
  customAttributes: s.record(s.string()).optional(),
  elements: s.array(element),
});

export type FormDefinition = Infer<typeof formDefinition>;
export type Element = Infer<typeof element>;
export type SectionElement = Infer<typeof section>;
export type ContainerElement = SectionElement | RepeatableGroupField;
